import React, { useState } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  Form,
  Button,
  Alert,
} from "react-bootstrap";
import { motion } from "framer-motion";
import axios from "axios";
import bgImage from "../assets/optContact.jpeg";

const Contact = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    message: "",
  });
  const [status, setStatus] = useState({ type: "", message: "" });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const professionalStyles = {
    heroSection: {
      background: `linear-gradient(rgba(0, 31, 63, 0.85), rgba(0, 51, 102, 0.85)), url(${bgImage})`,
      backgroundSize: "cover",
      backgroundPosition: "center",
      padding: "100px 50px",
      marginBottom: "80px",
    },
    heroTitle: {
      fontSize: "3.5rem",
      fontWeight: "700",
      letterSpacing: "-0.5px",
      marginBottom: "25px",
      color: "#fff",
      textTransform: "uppercase",
      textAlign: "center",
    },
    heroText: {
      fontSize: "1.2rem",
      maxWidth: "800px",
      margin: "0 auto",
      lineHeight: "1.8",
      fontWeight: "300",
      color: "#fff",
      textAlign: "center",
    },
    card: {
      border: "none",
      borderLeft: "4px solid #003366",
      background: "#fff",
      boxShadow: "0 2px 10px rgba(0,0,0,0.08)",
      borderRadius: "0",
      padding: "40px",
    },
    formLabel: {
      fontSize: "1rem",
      fontWeight: "600",
      color: "#003366",
      marginBottom: "10px",
      textTransform: "uppercase",
      letterSpacing: "0.5px",
    },
    formControl: {
      borderRadius: "0",
      border: "2px solid #e0e0e0",
      padding: "12px 15px",
      fontSize: "1rem",
      transition: "all 0.3s ease",
    },
    submitButton: {
      background: "#003366",
      border: "2px solid #003366",
      color: "#fff",
      padding: "15px 45px",
      fontSize: "1.1rem",
      fontWeight: "600",
      letterSpacing: "0.5px",
      textTransform: "uppercase",
      borderRadius: "0",
      transition: "all 0.3s ease",
      width: "100%",
    },
    infoCard: {
      border: "none",
      borderLeft: "4px solid #003366",
      background: "#f8f9fa",
      boxShadow: "0 2px 10px rgba(0,0,0,0.08)",
      borderRadius: "0",
      padding: "40px",
      height: "100%",
    },
    infoTitle: {
      fontSize: "1.8rem",
      fontWeight: "700",
      color: "#003366",
      marginBottom: "30px",
      textTransform: "uppercase",
      letterSpacing: "-0.5px",
    },
    infoText: {
      fontSize: "1.05rem",
      lineHeight: "2",
      color: "#555",
      marginBottom: "15px",
    },
    infoLink: {
      color: "#003366",
      textDecoration: "none",
      fontWeight: "600",
      transition: "all 0.3s ease",
    },
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setStatus({ type: "", message: "" });

    try {
      await axios.post(
        "https://opt-wise-backend.vercel.app/api/contact",
        formData
      );

      setStatus({
        type: "success",
        message: "Thank you for contacting us! We'll get back to you soon.",
      });
      setFormData({ name: "", email: "", message: "" });
    } catch (error) {
      setStatus({
        type: "error",
        message:
          error.response?.data?.message ||
          "Failed to send message. Please try again.",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Container fluid style={{ padding: 0 }}>
      {/* Hero Section */}
      <motion.div
        style={professionalStyles.heroSection}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.6 }}
      >
        <motion.h1
          style={professionalStyles.heroTitle}
          initial={{ opacity: 0, y: -30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2, duration: 0.8 }}
        >
          Contact Us
        </motion.h1>
        <motion.p
          style={professionalStyles.heroText}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.4, duration: 0.8 }}
        >
          Want to discuss how we can help with your investment goals? Our team
          is here to assist you every step of the way.
        </motion.p>
      </motion.div>

      {/* Contact Form and Info Section */}
      <Container style={{ paddingBottom: "80px" }}>
        <Row className="g-5">
          {/* Contact Form */}
          <Col lg={7}>
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
            >
              <Card style={professionalStyles.card}>
                <h2
                  style={{
                    fontSize: "2rem",
                    fontWeight: "700",
                    color: "#003366",
                    marginBottom: "30px",
                    textTransform: "uppercase",
                    letterSpacing: "-0.5px",
                  }}
                >
                  Send Us a Message
                </h2>

                {status.message && (
                  <Alert
                    variant={status.type === "success" ? "success" : "danger"}
                    style={{ borderRadius: "0" }}
                  >
                    {status.message}
                  </Alert>
                )}

                <Form onSubmit={handleSubmit}>
                  <Form.Group className="mb-4">
                    <Form.Label style={professionalStyles.formLabel}>
                      Your Name *
                    </Form.Label>
                    <Form.Control
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      placeholder="Enter your full name"
                      style={professionalStyles.formControl}
                    />
                  </Form.Group>

                  <Form.Group className="mb-4">
                    <Form.Label style={professionalStyles.formLabel}>
                      Email Address *
                    </Form.Label>
                    <Form.Control
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      placeholder="Enter your email address"
                      style={professionalStyles.formControl}
                    />
                  </Form.Group>

                  <Form.Group className="mb-4">
                    <Form.Label style={professionalStyles.formLabel}>
                      Message *
                    </Form.Label>
                    <Form.Control
                      as="textarea"
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      required
                      rows={6}
                      placeholder="Tell us about your investment goals..."
                      style={professionalStyles.formControl}
                    />
                  </Form.Group>

                  <Button
                    type="submit"
                    disabled={isSubmitting}
                    style={{
                      ...professionalStyles.submitButton,
                      opacity: isSubmitting ? 0.7 : 1,
                      cursor: isSubmitting ? "not-allowed" : "pointer",
                    }}
                    onMouseEnter={(e) => {
                      if (!isSubmitting) {
                        e.target.style.background = "#fff";
                        e.target.style.color = "#003366";
                      }
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.background = "#003366";
                      e.target.style.color = "#fff";
                    }}
                  >
                    {isSubmitting ? "Sending..." : "Send Message"}
                  </Button>
                </Form>
              </Card>
            </motion.div>
          </Col>

          {/* Contact Information */}
          <Col lg={5}>
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
            >
              <Card style={professionalStyles.infoCard}>
                <h3 style={professionalStyles.infoTitle}>
                  Optwise Group Office
                </h3>
                <div style={professionalStyles.infoText}>
                  <strong>Address:</strong>
                  <br />
                  Level 12, 141 Walker Street
                  <br />
                  Sydney NSW 2060
                  <br />
                  Australia
                </div>
                <div style={professionalStyles.infoText}>
                  <strong>Phone:</strong>
                  <br />
                  <a
                    href="tel:0290520803"
                    style={professionalStyles.infoLink}
                    onMouseEnter={(e) =>
                      (e.target.style.textDecoration = "underline")
                    }
                    onMouseLeave={(e) =>
                      (e.target.style.textDecoration = "none")
                    }
                  >
                    02 9052 0803
                  </a>
                </div>
                <div style={professionalStyles.infoText}>
                  <strong>Email:</strong>
                  <br />
                  <a
                    href="mailto:Admin@optwisegroup.com.au"
                    style={professionalStyles.infoLink}
                    onMouseEnter={(e) =>
                      (e.target.style.textDecoration = "underline")
                    }
                    onMouseLeave={(e) =>
                      (e.target.style.textDecoration = "none")
                    }
                  >
                    Admin@optwisegroup.com.au
                  </a>
                </div>
                <div
                  style={{
                    marginTop: "40px",
                    paddingTop: "30px",
                    borderTop: "2px solid #e0e0e0",
                  }}
                >
                  <h4
                    style={{
                      fontSize: "1.2rem",
                      fontWeight: "700",
                      color: "#003366",
                      marginBottom: "15px",
                      textTransform: "uppercase",
                    }}
                  >
                    Business Hours
                  </h4>
                  <div style={professionalStyles.infoText}>
                    Monday - Friday: 9:00 AM - 5:00 PM
                    <br />
                    Saturday - Sunday: Closed
                  </div>
                </div>
              </Card>
            </motion.div>
          </Col>
        </Row>
      </Container>
    </Container>
  );
};

export default Contact;
